import { createContext, useContext, useCallback, useRef } from 'react';

const AppointmentPersistenceContext = createContext();

/**
 * Provider for session-persistent appointment data
 * Survives navigation but gets cleared on app restart/refresh
 * Single export to avoid HMR issues
 */
const AppointmentPersistenceProvider = ({ children }) => {
    // Use useRef to persist data across navigation but not across sessions
    const persistenceStore = useRef(new Map());

    const persistFormState = useCallback((key, data) => {
        persistenceStore.current.set(key, {
            ...data,
            timestamp: Date.now()
        });
    }, []);

    const restoreFormState = useCallback((key) => {
        const stored = persistenceStore.current.get(key);
        
        if (stored) {
            const maxAge = 60 * 15 * 1000; // 15 minutes like the original
            if (Date.now() - stored.timestamp < maxAge) {
                // Remove after restore (same behavior as localStorage version)
                persistenceStore.current.delete(key);
                return stored;
            } else {
                // Expired - remove and return null
                persistenceStore.current.delete(key);
            }
        }
        
        return null;
    }, []);

    const clearFormState = useCallback((key) => {
        persistenceStore.current.delete(key);
    }, []);

    const clearAllFormStates = useCallback(() => {
        persistenceStore.current.clear();
    }, []);

    const value = {
        persistFormState,
        restoreFormState,
        clearFormState,
        clearAllFormStates
    };

    return (
        <AppointmentPersistenceContext value={value}>
            {children}
        </AppointmentPersistenceContext>
    );
};

// Attach the hook as a static property to avoid multiple exports
AppointmentPersistenceProvider.useContext = () => {
    const context = useContext(AppointmentPersistenceContext);
    if (!context) {
        throw new Error('AppointmentPersistenceProvider.useContext must be used within AppointmentPersistenceProvider');
    }
    return context;
};

export default AppointmentPersistenceProvider; 